import { useEffect, useRef, useState } from 'react';
import type { ApiClient } from './api';
import { AlertStrip } from './components/AlertStrip';
import { CommandPalette } from './components/CommandPalette';
import { PhoneAlerts } from './components/PhoneAlerts';
import { WidgetSetup } from './components/WidgetSetup';
import { Header, type ViewKey } from './components/Header';
import { AfrobotFab } from './components/AfrobotFab';
import { Sidebar } from './components/Sidebar';
import { StatusLine } from './components/StatusLine';
import { Ticker } from './components/Ticker';
import { usePoll } from './hooks';
import { ApprovalsView } from './views/ApprovalsView';
import { ChatView } from './views/ChatView';
import { BrandView } from './views/BrandView';
import { CompetitorsView } from './views/CompetitorsView';
import { BoardView } from './views/BoardView';
import { CalendarView } from './views/CalendarView';
import { GroupView } from './views/GroupView';
import type { Alert, ApprovalsResponse, BrandName, Me, Summary } from './types';

/**
 * The Shell (§2.1): one login, one read-only view across the three brands,
 * with deep links out to the platform admins for anything that needs
 * action. Nothing in this app writes to a platform; the deep link is the
 * only door out, and it opens in the admin where actioning belongs.
 */
/** Slack deep links land on #approvals/<id> (§14.1); the hash keeps the
 *  view stable across refresh without a router. */
/**
 * Every view the hash can name. Board and Calendar were missing until
 * 14 September 2026 - they went onto the nav without coming through
 * here - so #board did not route even on the occasions the fragment
 * survived. A list rather than a check per view, so adding a view and
 * forgetting its route is one omission instead of two.
 */
const HASH_VIEWS: ViewKey[] = [
  'group',
  'competitors',
  'board',
  'calendar',
  'chat',
  'cashblack',
  'afrofiliate',
  'equalishop',
];

export function parseHash(hash: string): { view: ViewKey | null; approvalId: string | null } {
  const match = /^#approvals(?:\/([0-9a-f-]{36}))?$/.exec(hash);
  if (match) return { view: 'approvals', approvalId: match[1] ?? null };
  const plain = hash.slice(1);
  if ((HASH_VIEWS as string[]).includes(plain)) {
    return { view: plain as ViewKey, approvalId: null };
  }
  return { view: null, approvalId: null };
}

export function App({
  api,
  me,
  mapStyleUrl,
  onSignOut,
}: {
  api: ApiClient;
  me: Me;
  mapStyleUrl: string;
  onSignOut: () => void;
}) {
  const canGroup = me.role === 'owner' || me.role === 'analyst';
  const fromHash = parseHash(window.location.hash);
  const [view, setView] = useState<ViewKey>(
    fromHash.view ?? (canGroup ? 'group' : (me.brands[0] ?? 'group')),
  );
  const [queueTarget] = useState<string | null>(fromHash.approvalId);
  const [linkNote, setLinkNote] = useState<string | null>(null);

  /**
   * The hash is the single source of truth for which view is open, and
   * the only writer of it.
   *
   * It used to be the other way round: a click set the view and wrote the
   * hash after it as a record, with replaceState, so the address bar
   * described the view without being able to change it. Typing #board did
   * nothing, back went nowhere, and a bookmark was decorative. Setting
   * the hash instead fires hashchange, which the effect below turns into
   * the view - so a click, a typed address, and the back button all
   * arrive by the same path and cannot disagree.
   */
  const selectView = (v: ViewKey) => {
    if (window.location.hash === `#${v}`) return; // no event, so set it here
    window.location.hash = v;
  };

  // The latest view, for the listener below, which is registered once and
  // would otherwise close over the view as it was at mount.
  const viewRef = useRef(view);
  viewRef.current = view;

  /**
   * One invariant: the address bar always describes the view on screen.
   *
   * An unparseable hash is the bar being wrong, not the view, so it is
   * corrected rather than obeyed - nobody gets thrown off the page they
   * are reading because they fat-fingered the URL. And a cold load with
   * no hash, or a junk one, stamps the view it resolved to, so the URL is
   * shareable without having to click something first.
   *
   * replaceState, never location.hash: it fires no event, so correcting
   * the bar cannot re-enter this listener, and a typo leaves no entry in
   * the history for the back button to walk into.
   */
  useEffect(() => {
    if (!parseHash(window.location.hash).view) {
      window.history.replaceState(null, '', `#${viewRef.current}`);
    }
    const onHashChange = () => {
      const next = parseHash(window.location.hash).view;
      if (next) {
        setView(next);
        return;
      }
      window.history.replaceState(null, '', `#${viewRef.current}`);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const alerts = usePoll<{ alerts: Alert[] }>(
    () => api.get('/api/alerts'),
    60_000,
    ['alerts', me.role !== 'restricted'],
  );

  const canApprove = me.role === 'owner' || me.role === 'operator';
  const pending = usePoll<ApprovalsResponse | null>(
    () => (canApprove ? api.get('/api/approvals') : Promise.resolve(null)),
    30_000,
    ['approvals-badge', canApprove, view],
  );

  // The KPI strip reads the summary the group view already polls, rather
  // than adding a call: same endpoint, same cadence, one more consumer.
  const summary = usePoll<Summary>(
    () => api.get('/api/dashboard/summary'),
    30_000,
    ['kpi-summary', me.role !== 'restricted'],
  );
  const voiceAvailable = typeof window !== 'undefined' && 'speechSynthesis' in window;

  if (me.role === 'restricted') {
    return (
      <div className="shell">
        <Header me={me} view={view} onView={selectView} onSignOut={onSignOut} />
        <div className="card pending-card" style={{ marginTop: 14 }}>
          <h3>Nothing here for the restricted role yet</h3>
          <p>
            Restricted access covers scoped slices such as the sales pipeline, which arrive with the
            CRM read model. The dashboards on this surface need the analyst role or above.
          </p>
        </div>
      </div>
    );
  }

  // A role whose brand scope is empty has nowhere to land: the group
  // dashboard is not theirs and there is no brand view to fall back to.
  // Before 11 September 2026 this crashed - the view fell back to 'group',
  // failed the canGroup check, dropped through to BrandView and passed
  // an undefined brand into BRAND_META, which is a white screen and a
  // TypeError rather than an answer. Say the true thing instead: the
  // account is sound, its scope is not set, and here is who fixes it.
  if (!canGroup && me.brands.length === 0) {
    return (
      <div className="shell">
        <Header me={me} view={view} onView={selectView} onSignOut={onSignOut} />
        <div className="card pending-card" style={{ marginTop: 14 }}>
          <h3>Your account has no brand assigned yet</h3>
          <p>
            You are signed in as {me.email ?? me.sub} with the {me.role} role, but no brand scope
            has been set on the account, so there is nothing this surface can show you yet. Ask an
            owner to set it; it takes a minute and you will not need to sign up again.
          </p>
        </div>
      </div>
    );
  }

  const deepLink = async (brand: BrandName, entity: string, id: string | null) => {
    try {
      const params = new URLSearchParams({ brand, entity });
      if (id) params.set('id', id);
      const { url } = await api.get<{ url: string }>(`/api/deeplink?${params}`);
      window.open(url, '_blank', 'noopener');
    } catch {
      setLinkNote(`The ${brand} admin URL is not configured yet, so there is nowhere to link to.`);
      window.setTimeout(() => setLinkNote(null), 5000);
    }
  };

  const badge = pending.data?.approvals.length;
  // "Home" is the group dashboard for a role that has one; a brand-scoped
  // user's landing page is their brand, and the group strips are not
  // theirs to see.
  const isHome = view === 'group' && canGroup;
  // Same rule the header and rail use: restricted has no chat surface,
  // so it gets no floating button either.
  const showChatSurface = me.role === 'owner' || me.role === 'operator' || me.role === 'analyst';

  return (
    <div className="shell cc-shell">
      <Header
        me={me}
        view={view}
        onView={selectView}
        onSignOut={onSignOut}
        pendingApprovals={badge}
        utility={
          <>
            <WidgetSetup api={api} me={me} />
            <PhoneAlerts api={api} me={me} />
          </>
        }
      />
      {showChatSurface && <AfrobotFab api={api} onAsk={() => selectView('chat')} />}
      <div className="cc-body">
        <Sidebar me={me} view={view} onView={selectView} pendingApprovals={badge} />
        <div className="cc-main">
      {/* The ticker rides on the dashboards, Home and each brand, and
        * stays off the pages where motion would pull the eye from work:
        * approvals, chat, the board and the calendar. */}
      {(isHome || view === 'cashblack' || view === 'afrofiliate' || view === 'equalishop') && (
        <Ticker api={api} />
      )}
      {/* Home only (Matthew, 4 September 2026), as the mock has them:
        * Approvals and Competitors keep the frame but not the dashboard
        * strips, so each page stays about its own job. */}
      {isHome && (
        <>
          <StatusLine
            live={
              alerts.error || summary.error
                ? false
                : alerts.data || summary.data
                  ? true
                  : null
            }
            lastUpdated={summary.data ? new Date() : null}
            voiceAvailable={voiceAvailable}
          />
          <AlertStrip alerts={alerts.data?.alerts ?? (alerts.error ? [] : null)} />
        </>
      )}
      <CommandPalette me={me} onView={selectView} onDeepLink={deepLink} />
      {linkNote && <div className="alert-quiet">{linkNote}</div>}
      {view === 'approvals' && canApprove ? (
        <ApprovalsView api={api} me={me} initialSelectedId={queueTarget} />
      ) : view === 'competitors' ? (
        <CompetitorsView api={api} me={me} />
      ) : view === 'board' ? (
        <BoardView api={api} me={me} />
      ) : view === 'calendar' ? (
        <CalendarView me={me} />
      ) : view === 'chat' ? (
        <ChatView api={api} me={me} />
      ) : view === 'group' && canGroup ? (
        <GroupView
          api={api}
          me={me}
          mapStyleUrl={mapStyleUrl}
          onDeepLink={deepLink}
          onAsk={() => selectView('chat')}
        />
      ) : (
        <BrandView
          api={api}
          me={me}
          brand={view === 'group' || view === 'approvals' ? (me.brands[0] as BrandName) : view}
          mapStyleUrl={mapStyleUrl}
          onDeepLink={deepLink}
        />
      )}
        </div>
      </div>
    </div>
  );
}
